/**
 * The name grammar (D182, D226, D229): the article a name takes, and the
 * sentences the engine composes from names without a slot.
 *
 * @remarks
 * Split out of `default-responses.ts` at the file-size cap when the
 * which-one ask's sentence (D229, E14) joined the search reveal's and the
 * chips' (D226, E9) here. One idea, named once: whether a name takes "the"
 * is a property of the WORDS, read off whichever item carries them, and
 * every sentence the engine writes by hand reads it from the same place the
 * `{itemName}` slot does.
 *
 * @packageDocumentation
 */

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

/**
 * D182: the name about to fill `{itemName}`, when it refuses an article. A
 * property of the WORDS, read off whichever item carries them — two items
 * sharing one name share its grammar — so every call site keeps passing
 * `item.name` and none of them has to know.
 *
 * @param bundle - The loaded bundle
 * @param itemName - The name being interpolated, when the slot names one
 * @returns The name when the "the" written before its placeholder must go; otherwise nothing
 */
export const articleFreeNameOf = (bundle: EngineGameBundle, itemName: string | undefined): string | undefined =>
	itemName !== undefined &&
	(opensWithAnArticle(itemName) ||
		bundle.contentPack.items.some((item) => item.name === itemName && !item.nameTakesArticle) ||
		// D232: an actor's name refuses the article as an item's does — a proper noun is one wherever it stands.
		bundle.contentPack.actors.some((actor) => actor.name === itemName && !actor.nameTakesArticle))
		? itemName
		: undefined;

/**
 * D231 (Calder's P7 §2): a name that already begins with "the" — the
 * gatehouse's `the facility`, `the rain` — takes no second one, whatever its
 * `nameTakesArticle` says; "The the facility" is never a sentence anyone
 * meant. Read case-insensitively, so a sentence-opening "The" counts.
 *
 * @param name - A display name
 * @returns Whether the name carries its own article
 */
const opensWithAnArticle = (name: string): boolean => /^the\s/iu.test(name);

/**
 * A name as a sentence says it (D226, E9): "the kettle", "Sylvain", "the
 * room itself" — the article D182 decides for `{itemName}`, available to the
 * lines the engine composes WITHOUT a slot: the search reveal, the export's
 * "which one?" sentence, the chips under a which-one question. Before this
 * each of those wrote `the ${name}` by hand and read "the Sylvain" while the
 * slots beside them read "Sylvain".
 *
 * @param bundle - The loaded bundle (whose items say which names refuse the article)
 * @param name - A display name — an item's, or the engine's framing for the room or the hands
 * @returns The name with "the" before it when it takes one
 */
export const nameInSentence = (bundle: EngineGameBundle, name: string): string =>
	articleFreeNameOf(bundle, name) === undefined ? `the ${name}` : name;

/**
 * The answers a which-one ask offers, as one question sentence: "The intact
 * locker, or the wall locker?" — display names, presented order, each with
 * the article it takes (D226, E9: "Sylvain, or the axe?"). D229 (E14): the
 * ask's own line says it, through the `clarificationQuestion` slot's
 * `{options}`; the transcript export composes it for a line that did not.
 * The shout rule uppercases it wherever the ask is system output.
 *
 * @param bundle - The loaded bundle (which names refuse the article)
 * @param offeredAnswers - The display names the ask offered (two or more, by the question schema's own law)
 * @returns The sentence
 */
export const offeredAnswersSentence = (bundle: EngineGameBundle, offeredAnswers: readonly string[]): string => {
	const named = offeredAnswers.map((name) => nameInSentence(bundle, name));
	// slice/join keeps every read total — the schema's min(2) is the law, and
	// no index here needs an undefined-guard to honor it.
	const listed = `${named.slice(0, -1).join(', ')}, or ${named.slice(-1).join('')}?`;

	return `${listed.charAt(0).toUpperCase()}${listed.slice(1)}`;
};

/**
 * D182: the article before `{itemName}` removed, the name put in its place.
 * Mid-sentence the name goes in as written — "Nothing about yourself answers
 * to KICK". Opening a sentence the article was carrying the capital, so the
 * name takes it — "Yourself is not something you can wear" — and no line
 * starts lowercase because its subject changed grammar.
 *
 * @param text - The line, placeholders unfilled
 * @param name - The article-free name
 * @returns The line with that placeholder filled wherever an article preceded it
 */
export const dropArticleBefore = (text: string, name: string): string =>
	text.replaceAll(/\b([Tt])he \{itemName\}/gu, (_wholeMatch, article: string) =>
		article === 'T' ? `${name.charAt(0).toUpperCase()}${name.slice(1)}` : name,
	);
